/**
 * QR code detection service.
 * Wraps the jsQR library for browser-based QR code detection.
 *
 * Only detects QR codes with the "peekbox:" prefix to avoid
 * false positives from random QR codes in the environment.
 */

import jsQR from 'jsqr';
import type { DetectedMarker, Point2D } from './types';

/** Prefix required in QR data for Peekbox boxes */
export const QR_PREFIX = 'peekbox:';

/**
 * QR code detector. Stateless — each call to detect() is independent.
 */
export class QrDetector {
  /**
   * Detect QR codes in an ImageData frame.
   * Only returns QR codes whose data starts with "peekbox:".
   * jsQR detects at most one QR code per frame.
   */
  detect(imageData: ImageData): DetectedMarker[] {
    const result = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: 'dontInvert',
    });

    if (!result) return [];

    // Only accept Peekbox QR codes
    if (!result.data.startsWith(QR_PREFIX)) return [];

    const markerId = result.data.slice(QR_PREFIX.length);
    if (markerId.length === 0) return [];

    const loc = result.location;
    const corners: [Point2D, Point2D, Point2D, Point2D] = [
      { x: loc.topLeftCorner.x, y: loc.topLeftCorner.y },
      { x: loc.topRightCorner.x, y: loc.topRightCorner.y },
      { x: loc.bottomRightCorner.x, y: loc.bottomRightCorner.y },
      { x: loc.bottomLeftCorner.x, y: loc.bottomLeftCorner.y },
    ];

    return [
      {
        markerType: 'qr',
        markerId,
        corners,
      },
    ];
  }
}
